import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWebAuth } from "@/lib/webAuth";
import webApi from "@/lib/webApi";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Coins, Copy, Upload, Loader2, CheckCircle2, Wrench } from "lucide-react";
import { toast } from "sonner";

export default function WebBuy() {
  const { user, loading } = useWebAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [bankInfo, setBankInfo] = useState({});
  const [purchasesDisabled, setPurchasesDisabled] = useState(false);
  const [order, setOrder] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    webApi.get("/packages").then((r) => {
      setPackages(r.data.packages || []);
      setBankInfo(r.data.bank_info || {});
      setPurchasesDisabled(!!r.data.purchases_disabled);
    });
  }, []);

  const createOrder = async (pkg) => {
    if (purchasesDisabled) {
      toast.error("🔧 Tienda en mantenimiento — vuelve pronto.");
      return;
    }
    try {
      const r = await webApi.post("/orders", { package_id: pkg.id });
      setOrder(r.data);
      toast.success("Orden creada. Realiza la transferencia y sube tu comprobante.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creando la orden");
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado");
  };

  const uploadProof = async () => {
    if (!proofFile) {
      toast.error("Selecciona un archivo");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", proofFile);
      fd.append("note", note);
      await webApi.post(`/orders/${order.id}/proof`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Comprobante enviado. Te avisamos cuando se apruebe.");
      navigate("/ordenes");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !user) return null;

  return (
    <div className="min-h-[100dvh] bg-zinc-950 text-zinc-100">
      <header
        className="h-14 flex-shrink-0 bg-zinc-950/85 backdrop-blur-xl border-b border-zinc-900 flex items-center px-3 sm:px-6 gap-3 sticky top-0 z-10"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/chat")}
          data-testid="buy-back-btn"
          className="text-zinc-400 hover:text-zinc-100"
        >
          <ArrowLeft size={16} className="sm:mr-1" />
          <span className="hidden sm:inline">Chat</span>
        </Button>
        <div className="flex-1" />
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-sm bg-zinc-900 border border-zinc-800">
          <Coins size={13} className="text-amber-400" />
          <span className="text-xs font-mono font-bold">{user.credits ?? user.credits_web ?? 0}</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="overline mb-2">Tu cuenta · Web</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-2">
          Compra créditos
        </h1>
        <p className="text-sm text-zinc-500 mb-8">
          Transferencia bancaria. Aprobación manual en menos de 2 horas.
        </p>

        {purchasesDisabled && (
          <div
            data-testid="purchases-disabled-banner"
            className="mb-8 border border-amber-500/40 bg-amber-500/5 rounded-md p-5 flex items-start gap-3"
          >
            <Wrench size={20} className="text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-bold text-amber-300 mb-1">Tienda en mantenimiento</div>
              <p className="text-xs text-zinc-400 leading-relaxed">
                Estamos ajustando precios y promociones. Los paquetes se muestran como referencia
                pero los botones de compra están temporalmente desactivados. Vuelve en unas horas.
                Tus créditos actuales y todos los comandos siguen funcionando con normalidad.
              </p>
            </div>
          </div>
        )}

        {!order ? (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {packages.map((p) => (
                <div
                  key={p.id}
                  className={
                    "border rounded-md p-5 transition-colors " +
                    (purchasesDisabled
                      ? "border-zinc-900 bg-zinc-900/30 opacity-60"
                      : "border-zinc-800 bg-zinc-900/50 hover:border-[#2AABEE]/50")
                  }
                  data-testid={`pkg-card-${p.id}`}
                >
                  <div className="overline text-[#2AABEE] mb-2">
                    {p.name.toUpperCase()}
                  </div>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="font-display font-black text-3xl">
                      {p.credits}
                    </span>
                    <span className="text-zinc-500 text-sm">créditos</span>
                  </div>
                  <div className="text-2xl font-bold text-zinc-300 mb-4">
                    ${p.price_usd}{" "}
                    <span className="text-xs text-zinc-500 font-normal">USD</span>
                  </div>
                  <div className="text-xs text-zinc-500 mb-4">
                    ≈ ${p.price_mxn} MXN
                  </div>
                  <Button
                    onClick={() => createOrder(p)}
                    disabled={purchasesDisabled}
                    data-testid={`pkg-buy-${p.id}`}
                    className={
                      "w-full rounded-sm " +
                      (purchasesDisabled
                        ? "bg-zinc-800 text-zinc-600 cursor-not-allowed hover:bg-zinc-800"
                        : "bg-[#2AABEE] hover:bg-[#229ED9] text-white")
                    }
                  >
                    {purchasesDisabled ? "Mantenimiento" : "Comprar"}
                  </Button>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="space-y-6">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-md p-4 flex gap-3">
              <CheckCircle2 className="text-emerald-400 flex-shrink-0" size={20} />
              <div>
                <div className="font-bold text-emerald-300">Orden creada</div>
                <div className="text-xs text-emerald-400/80">
                  Ref: <code className="font-mono">{order.reference}</code> ·{" "}
                  {order.credits_or_days} créditos por ${order.price_usd} USD
                </div>
              </div>
            </div>

            <div className="border border-zinc-800 bg-zinc-900/50 rounded-md p-5">
              <h3 className="font-display font-black text-xl mb-4">
                1. Realiza la transferencia
              </h3>
              <div className="space-y-3">
                {[
                  ["Banco", bankInfo.bank_name || "BANREGIO"],
                  ["Beneficiario", bankInfo.beneficiary || "—"],
                  ["CLABE", bankInfo.clabe || "—"],
                  ["Cuenta", bankInfo.account_number || "—"],
                  ["Concepto", `IARisk-${order.reference}`],
                  ["Monto MXN", `$${(order.price_usd * 20).toFixed(0)}`],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="flex items-center justify-between bg-black/40 rounded-sm px-3 py-2"
                  >
                    <div className="text-xs text-zinc-500">{label}</div>
                    <div className="flex items-center gap-2">
                      <code className="text-xs font-mono text-zinc-300">
                        {value}
                      </code>
                      <button
                        onClick={() => copy(String(value))}
                        className="text-zinc-500 hover:text-[#2AABEE]"
                        data-testid={`copy-${label}`}
                      >
                        <Copy size={11} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-zinc-800 bg-zinc-900/50 rounded-md p-5">
              <h3 className="font-display font-black text-xl mb-4">
                2. Sube tu comprobante
              </h3>
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                data-testid="proof-file-input"
                className="block w-full text-xs text-zinc-400 file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:text-xs file:font-bold file:bg-zinc-800 file:text-zinc-100 hover:file:bg-zinc-700 mb-4"
              />
              <Textarea
                placeholder="Nota opcional (ej: 'Pagué desde BBVA, llega en 1 día hábil')"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="proof-note-input"
                className="bg-black/40 border-zinc-800 rounded-sm text-sm mb-4"
                rows={2}
              />
              <Button
                onClick={uploadProof}
                disabled={!proofFile || submitting}
                data-testid="proof-submit-btn"
                className="w-full bg-[#2AABEE] hover:bg-[#229ED9] text-white rounded-sm"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Upload size={14} className="mr-2" />
                )}
                Enviar comprobante
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
